import { useEffect, useMemo, useRef, useState } from "react";
import "./Section_services.css";

type Service = {
  id: string;
  title: string;
  subtitle: string;
  bullets: string[];
  tag: string;
};

const Services = () => {
  const services: Service[] = useMemo(
    () => [
      {
        id: "diseno",
        title: "Diseño Arquitectónico",
        subtitle: "Concepto y ejecución visual para espacios funcionales y modernos.",
        bullets: [
          "Proyectos ejecutivos",
          "Rediseño de espacios",
          "Diseño y construcción de mobiliario",
          "Render y propuestas visuales",
        ],
        tag: "Arquitectura",
      },
      {
        id: "obra-civil",
        title: "Obra Civil",
        subtitle: "Construcción y adecuación con enfoque en calidad y tiempos.",
        bullets: ["Nuevas edificaciones", "Remodelaciones", "Adecuación de espacios"],
        tag: "Construcción",
      },
      {
        id: "electricas",
        title: "Instalaciones Eléctricas",
        subtitle: "Diseño e instalación segura para uso residencial e industrial.",
        bullets: ["Residenciales", "Industriales"],
        tag: "Energía",
      },
      {
        id: "especiales",
        title: "Instalaciones Especiales",
        subtitle: "Sistemas críticos para confort, seguridad y operación.",
        bullets: [
          "Sistemas de Aire Acondicionado",
          "CCTV y monitoreo",
          "Sistemas Contra Incendios",
          "PCI",
          "Sistemas hidráulicos",
        ],
        tag: "Infraestructura",
      },
      {
        id: "telecom",
        title: "Telecomunicaciones",
        subtitle: "Conectividad y transporte de datos para operación continua.",
        bullets: [
          "Sitios RF celulares",
          "Enlaces de microondas",
          "Redes de Fibra Óptica",
          "Sistemas de radio trunking",
        ],
        tag: "Redes",
      },
      {
        id: "tech",
        title: "Implementaciones Tecnológicas",
        subtitle: "Integración de sistemas para control, comunicación y experiencia.",
        bullets: [
          "Control y Automatización de Accesos",
          "Redes Inalámbricas",
          "Sistemas de Audio y voceo",
          "Sistemas Videowall",
          "Redes ethernet empresariales/industriales",
        ],
        tag: "Tecnología",
      },
    ],
    []
  );

  const gridRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  // Reveal al entrar (sin librerías)
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setInView(true);
      },
      { threshold: 0.15, rootMargin: "0px 0px -12% 0px" }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Tilt 3D suave (desktop)
  useEffect(() => {
    const finePointer = window.matchMedia("(pointer: fine)").matches;
    if (!finePointer) return;

    const root = gridRef.current;
    if (!root) return;

    const cards = Array.from(root.querySelectorAll<HTMLElement>(".svc-card"));

    const onMove = (e: MouseEvent) => {
      const card = e.currentTarget as HTMLElement;
      const r = card.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;

      const rx = ((y / r.height) - 0.5) * -10;
      const ry = ((x / r.width) - 0.5) * 10;

      card.style.setProperty("--rx", `${rx}deg`);
      card.style.setProperty("--ry", `${ry}deg`);
      card.style.setProperty("--glow-x", `${(x / r.width) * 100}%`);
      card.style.setProperty("--glow-y", `${(y / r.height) * 100}%`);
    };

    const onLeave = (e: MouseEvent) => {
      const card = e.currentTarget as HTMLElement;
      card.style.setProperty("--rx", `0deg`);
      card.style.setProperty("--ry", `0deg`);
    };

    cards.forEach((c) => {
      c.addEventListener("mousemove", onMove);
      c.addEventListener("mouseleave", onLeave);
    });

    return () => {
      cards.forEach((c) => {
        c.removeEventListener("mousemove", onMove);
        c.removeEventListener("mouseleave", onLeave);
      });
    };
  }, []);

  return (
    <div className="svc">
      <div className="svc-head">
        <p className="svc-kicker">Nuestros servicios</p>
        <h2 className="svc-title">Soluciones Integrales</h2>
        <p className="svc-subtitle">
          Arquitectura, obra e implementaciones tecnológicas para proyectos completos, listos para operar.
        </p>
      </div>

      <div className={`svc-grid ${inView ? "is-inview" : ""}`} ref={gridRef}>
        {services.map((s, idx) => (
          <article
            key={s.id}
            className="svc-card"
            style={{ ["--delay" as any]: `${idx * 70}ms` }}
          >
            <div className="svc-card-top">
              <span className="svc-tag">{s.tag}</span>

              {/* “3D orb” */}
              <div className="svc-orb" aria-hidden="true">
                <span className="svc-orb-inner" />
              </div>
            </div>

            <h3 className="svc-card-title">{s.title}</h3>
            <p className="svc-card-sub">{s.subtitle}</p>

            <ul className="svc-bullets">
              {s.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>

            <div className="svc-card-actions">
              <a className="svc-link" href="/#contacto" aria-label={`Solicitar propuesta para ${s.title}`}>
                Solicitar propuesta
              </a>
              <a className="svc-link ghost" href="/#proyectos" aria-label={`Ver proyectos relacionados con ${s.title}`}>
                Ver proyectos
              </a>
            </div>

            <span className="svc-glow" aria-hidden="true" />
          </article>
        ))}
      </div>

      <div className="svc-cta">
        <div className="svc-cta-box">
          <div>
            <h3 className="svc-cta-title">¿Te preparamos una propuesta?</h3>
            <p className="svc-cta-sub">
              Comparte tu objetivo y armamos una solución integral con alcance, tiempos y entregables.
            </p>
          </div>
          <a className="svc-cta-btn" href="/#contacto">
            Contactar
          </a>
        </div>
      </div>
    </div>
  );
};

export default Services;